/**
 * CalculatorWindow — the in-app calculator as a window over the practice view
 * (Student Tools Figma, "Pin & Dock", node 3309:28554).
 *
 * One fixed-position container, three CSS states (floating / docked /
 * expanded). The container never unmounts across a mode change, so switching
 * modes never throws away the student's work.
 *
 * Everything lives inside the document. Assignment practice runs Secure Mode,
 * which auto-submits on `fullscreenchange` and on the tab going hidden — so a
 * real browser window (`window.open`, Document PiP) or the Fullscreen API for
 * "expanded" would end the student's assignment.
 */

import classnames from 'classnames';
import React, {
  Suspense,
  lazy,
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  useContext
} from 'react';
import {createPortal} from 'react-dom';
import {ErrorBoundary} from 'react-error-boundary';
import {useTracking} from 'react-tracking';

import {IconButton, LoadingSpinner, Text} from '@albert-io/atomic';
import {
  GlobalNavBehaviorContext,
  GlobalNavBehaviorEnum
} from 'client/GlobalNavigation/GlobalNavBehavior.context';

import type {CalculatorType} from './CalculatorTitleBar.react';
import {CALCULATOR_NAMES, CalculatorTypeAnnouncer} from './CalculatorTypeAnnouncer';
import type {CalculatorWindowMode} from './calculatorWindow.store';
import {CALCULATOR_DOCK_INLINE_SIZE_VAR, useCalculatorWindowStore} from './calculatorWindow.store';
import {
  DOCK_MIN_HOST_PX,
  dockInlineSize,
  dockWidthText,
  useCalculatorDockResize
} from './useCalculatorDockResize';
import {useCalculatorDrag} from './useCalculatorDrag';
import {useIsCalculatorAvailable} from './useIsCalculatorAvailable';
import {useF6WindowFocus} from './useF6WindowFocus';
import {useTypeSwitchFocus} from './useTypeSwitchFocus';
import {calculatorTypesFor} from './calculatorTier';

const BasicCalculatorBody = lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "BasicCalculator" */ 'client/BasicCalculator/BasicCalculator.react'
    )
  ).BasicCalculatorBody
}));

const GraphingCalculatorBody = lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "GraphingCalculator" */ 'client/GraphingCalculator/GraphingCalculator.react'
    )
  ).GraphingCalculatorBody
}));

const ScientificCalculatorBody = lazy(async () => ({
  default: (
    await import(
      /* webpackChunkName: "ScientificCalculator" */ 'client/ScientificCalculator/ScientificCalculator.react'
    )
  ).ScientificCalculatorBody
}));

/**
 * Per-type window width, from the Figma frames — the two calculators are
 * different shapes. Declared twice because Tailwind only sees class names it
 * can read literally in the source, while the docked width needs the number;
 * `WIDTH_CLASS` and `WIDTH_PX` must stay in step (asserted in tests).
 *
 * `WIDTH_CLASS` is floating-only: a docked window is resizable, so it takes its
 * width from the dock custom property instead.
 */
export const WIDTH_PX: Record<CalculatorType, number> = {
  basic: 426,
  graphing: 426,
  scientific: 585
};
export const WIDTH_CLASS: Record<CalculatorType, string> = {
  basic: 'w-[426px]',
  graphing: 'w-[426px]',
  scientific: 'w-[585px]'
};

const subscribeToViewport = (onChange: () => void) => {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
};
const getViewportWidth = () => window.innerWidth;

/**
 * Floating-mode height. Basic and scientific are their measured content plus
 * the window's 1px top and bottom border, since the box is `border-box` and a
 * bare content height would leave them permanently 2px short. Graphing is the
 * Figma frame instead — a canvas has no height of its own to measure.
 */
export const HEIGHT_CLASS: Record<CalculatorType, string> = {
  basic: 'h-[726px]',
  graphing: 'h-[658px]',
  scientific: 'h-[617px]'
};

interface WindowControlsProps {
  mode: CalculatorWindowMode;
  /** Where the fullscreen button returns to on leaving fullscreen. */
  previousMode: Exclude<CalculatorWindowMode, 'expanded'>;
  onSetMode: (mode: CalculatorWindowMode) => void;
  onClose: () => void;
  /** Docking and floating are desktop-only; a phone gets the expanded window. */
  canDock: boolean;
}

/**
 * The title bar sits at the top of the window, and two of the three modes pin
 * that to the viewport's top edge — so a tooltip must hang below its button.
 * Above, the popper has nowhere to go but inside the calculator, over the
 * display it is meant to describe.
 */
const TITLE_BAR_TOOLTIP_PROPS = {placement: 'bottom'} as const;

/** Anything in the title bar that owns its own pointer press. */
const INTERACTIVE = 'button, a, input, select, textarea, [role="menuitem"]';

/** How far a title-bar press can travel and still count as a click, not a drag. */
const BAR_CLICK_SLOP_PX = 4;

/**
 * The dock/float and fullscreen buttons are actions, not toggles: each icon,
 * name and tooltip describes what a click will do, so neither carries
 * `aria-pressed`. Each name contains its tooltip, so a voice-control user can
 * say what they see.
 */
const DOCK_FLOAT_ACTION = {
  floating: {
    icon: 'sidebar-flip',
    label: 'Dock to side',
    tooltip: 'Dock to side',
    target: 'docked'
  },
  docked: {
    icon: 'window-restore',
    label: 'Float calculator over the page',
    tooltip: 'Float',
    target: 'floating'
  }
} as const;

const FULLSCREEN_ACTION = {
  enter: {icon: 'expand', label: 'Fullscreen'},
  exit: {icon: 'compress', label: 'Exit fullscreen'}
} as const;

/** The title bar's window buttons. Fullscreen shows only its own exit button. */
export const WindowControls = ({
  mode,
  previousMode,
  onSetMode,
  onClose,
  canDock
}: WindowControlsProps) => {
  const isFullscreen = canDock && mode === 'expanded';
  const fullscreen = FULLSCREEN_ACTION[isFullscreen ? 'exit' : 'enter'];

  return (
    <>
      {canDock && mode !== 'expanded' && (
        <IconButton
          color='blend-dark'
          data-testid='calculator-window__mode-toggle'
          icon={DOCK_FLOAT_ACTION[mode].icon}
          iconStyle='regular'
          label={DOCK_FLOAT_ACTION[mode].label}
          tooltip={DOCK_FLOAT_ACTION[mode].tooltip}
          tooltipProps={TITLE_BAR_TOOLTIP_PROPS}
          onClick={() => onSetMode(DOCK_FLOAT_ACTION[mode].target)}
          size='s'
        />
      )}
      {canDock && (
        <IconButton
          color='blend-dark'
          data-testid='calculator-window__expand'
          icon={fullscreen.icon}
          iconStyle='regular'
          label={fullscreen.label}
          tooltip={fullscreen.label}
          tooltipProps={TITLE_BAR_TOOLTIP_PROPS}
          onClick={() => onSetMode(isFullscreen ? previousMode : 'expanded')}
          size='s'
        />
      )}
      {!isFullscreen && (
        <IconButton
          color='blend-dark'
          data-testid='calculator-window__close'
          icon='xmark'
          iconStyle='regular'
          label='Close calculator'
          tooltip='Close'
          tooltipProps={TITLE_BAR_TOOLTIP_PROPS}
          onClick={onClose}
          size='s'
        />
      )}
    </>
  );
};

// The sticky global nav is $horizontal-navigation-height (60px), which no spacing token matches.
const GLOBAL_NAV_HEIGHT_PX = 60;
const FLOATING_BELOW_NAV_CLASS =
  'top-[calc(var(--spacing-x11)+60px)] max-h-[calc(100dvh-var(--spacing-x12)-60px)]';
const DOCKED_BELOW_NAV_CLASS = 'top-[60px]';

interface CalculatorWindowProps {
  /** Phones get the expanded window only — a 426px draggable box is unusable there. */
  isMobile?: boolean;
  /** Room the host page keeps beside a docked rail; a widened rail gives it back. */
  hostMinWidth?: number;
}

export const CalculatorWindow = ({
  isMobile = false,
  hostMinWidth = DOCK_MIN_HOST_PX
}: CalculatorWindowProps) => {
  const isAvailable = useIsCalculatorAvailable();
  // Assignments keep the sticky global nav; adaptive hides it.
  const isBelowGlobalNav =
    useContext(GlobalNavBehaviorContext).navBehavior === GlobalNavBehaviorEnum.VISIBLE_STICKY;
  const isOpen = useCalculatorWindowStore((state) => state.isOpen);
  // Deferred together, so a switch keeps the current calculator on screen until the next one's
  // chunk has loaded.
  const type = useDeferredValue(useCalculatorWindowStore((state) => state.type));
  const tier = useCalculatorWindowStore((state) => state.tier);
  const storedMode = useCalculatorWindowStore((state) => state.mode);
  const previousMode = useCalculatorWindowStore((state) => state.previousMode);
  const position = useCalculatorWindowStore((state) => state.position);
  const dockedWidth = useCalculatorWindowStore((state) => state.dockedWidth);
  const setType = useCalculatorWindowStore((state) => state.setType);
  const setMode = useCalculatorWindowStore((state) => state.setMode);
  const setPosition = useCalculatorWindowStore((state) => state.setPosition);
  const setDockedWidth = useCalculatorWindowStore((state) => state.setDockedWidth);
  const close = useCalculatorWindowStore((state) => state.close);
  const {trackEvent} = useTracking();
  // Populated on open and on each type switch, so a calculator's chunk is
  // fetched when the student first selects it rather than on every open.
  const mountedTypes = useDeferredValue(useCalculatorWindowStore((state) => state.mountedTypes));

  const selectType = useCallback(
    (nextType: CalculatorType) => {
      setType(nextType);
      trackEvent({
        event: 'switch',
        type: 'dropdown item',
        component: 'calculator',
        content: nextType
      });
    },
    [setType, trackEvent]
  );

  const changeMode = useCallback(
    (nextMode: CalculatorWindowMode) => {
      setMode(nextMode);
      trackEvent({
        event: 'click',
        type: 'button',
        component: 'calculator_window',
        content: nextMode === 'expanded' ? 'fullscreen' : nextMode
      });
    },
    [setMode, trackEvent]
  );

  const windowRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<Element | null>(null);
  const barPressRef = useRef<{x: number; y: number} | null>(null);

  const mode = isMobile ? 'expanded' : storedMode;
  // Wider than visible: an opened calculator stays in the tree while hidden,
  // since its work lives in its own state. Only reset() empties this.
  const isMounted = isAvailable && mountedTypes.length > 0;
  const isRendered = isMounted && isOpen;

  const drag = useCalculatorDrag({
    position,
    onCommit: setPosition,
    windowRef,
    // A closed window stays mounted as `display: none`, which measures 0x0.
    enabled: isRendered && mode === 'floating',
    topInset: isBelowGlobalNav ? GLOBAL_NAV_HEIGHT_PX : 0
  });

  const resize = useCalculatorDockResize({
    width: dockedWidth,
    defaultWidth: WIDTH_PX[type],
    onCommit: setDockedWidth,
    windowRef,
    enabled: mode === 'docked',
    hostMinWidth
  });

  // The rail's width tracks the viewport in CSS, but the range the splitter
  // announces is React state, so it needs its own subscription to stay true.
  const viewportWidth = useSyncExternalStore(subscribeToViewport, getViewportWidth);
  const dockMax = Math.max(WIDTH_PX[type], viewportWidth - hostMinWidth);
  // Clamped like the CSS is, so the announced width can never exceed
  // the announced maximum.
  const dockValue = Math.min(dockedWidth ?? WIDTH_PX[type], dockMax);

  // Re-applied after any change that re-creates the node's inline styles; the
  // drag itself writes these directly (see useCalculatorDrag).
  useEffect(() => {
    const element = windowRef.current;
    element?.style.setProperty('--calculator-window-x', `${position.x}px`);
    element?.style.setProperty('--calculator-window-y', `${position.y}px`);
  }, [position.x, position.y, isRendered, mode]);

  // `clamp` rather than a measured width so the upper bound tracks the viewport
  // without a resize listener; a layout effect so the first docked paint does
  // not flash the class's fallback width.
  useLayoutEffect(() => {
    if (!isRendered || mode !== 'docked') {
      return;
    }
    const root = document.documentElement;
    root.style.setProperty(
      CALCULATOR_DOCK_INLINE_SIZE_VAR,
      dockInlineSize(WIDTH_PX[type], dockedWidth ?? WIDTH_PX[type], hostMinWidth)
    );
    return () => {
      root.style.removeProperty(CALCULATOR_DOCK_INLINE_SIZE_VAR);
    };
  }, [isRendered, mode, type, dockedWidth, hostMinWidth]);

  // Expanded covers the whole viewport, so tabbing out of the window lands on
  // controls the student cannot see. Only that mode contains focus — floating
  // and docked are deliberately non-modal, because working the question beside
  // the calculator is the point of them. The app root is a sibling of this
  // portal, so marking it inert takes the entire page behind out of the tab
  // order. Cleanup runs on close, unmount, and on leaving expanded; leaving it
  // set would make the page unusable.
  useEffect(() => {
    if (!isRendered || mode !== 'expanded') {
      return;
    }
    const appRoot = document.getElementById('app');
    if (!appRoot) {
      return;
    }
    appRoot.inert = true;
    return () => {
      appRoot.inert = false;
    };
  }, [isRendered, mode]);

  // Move focus into the window on open and hand it back to whatever opened it
  // on close, so a keyboard user isn't dropped at the top of the document.
  useEffect(() => {
    if (!isRendered) {
      return;
    }
    const windowElement = windowRef.current;
    openerRef.current = document.activeElement;
    windowElement?.focus();

    return () => {
      const active = document.activeElement;
      const focusWouldBeStranded =
        !active || active === document.body || Boolean(windowElement?.contains(active));
      const opener = openerRef.current;
      if (focusWouldBeStranded && opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [isRendered]);

  const visibleBodyRef = useTypeSwitchFocus({type, isRendered, windowRef});
  // Expanded is modal and inerts the page, so F6 has nowhere to jump to.
  useF6WindowFocus({isRendered, enabled: mode !== 'expanded', windowRef, openerRef});

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Only from the window itself or its title bar: inside the calculator, Escape
      // belongs to the calculator (it exits graphing trace mode).
      const {target} = event;
      const isChrome =
        target === event.currentTarget ||
        (target instanceof Element && target.closest('[data-calculator-title-bar]') !== null);
      if (event.key !== 'Escape' || event.defaultPrevented || !isChrome) {
        return;
      }
      if (mode === 'expanded' && !isMobile) {
        changeMode(previousMode);
      } else {
        close();
      }
    },
    [changeMode, close, isMobile, mode, previousMode]
  );

  if (!isMounted) {
    return null;
  }

  // The window chrome goes to the visible calculator only. Both bodies stay
  // mounted, so rendering it into each would put two of every control — and two
  // of every `data-testid` — in the DOM, one of them hidden and unclickable.
  const bodyRefFor = (calculatorType: CalculatorType) =>
    calculatorType === type ? visibleBodyRef : undefined;

  const chromeFor = (calculatorType: CalculatorType) =>
    calculatorType === type
      ? {
          dragProps:
            mode === 'floating'
              ? {
                  'aria-label': 'Move calculator. Use the arrow keys to reposition it.',
                  className:
                    'cursor-grab touch-none active:cursor-grabbing hover:background-brand-bold-hover focus-ring-inset',
                  'data-testid': 'calculator-window__drag-handle',
                  tabIndex: 0,
                  ...drag,
                  // Controls inside the bar own their own press, or pointer
                  // capture on the header swallows their click.
                  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
                    if (event.target === event.currentTarget) {
                      drag.onKeyDown(event);
                    }
                  },
                  onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
                    barPressRef.current = null;
                    if (!(event.target instanceof Element) || !event.target.closest(INTERACTIVE)) {
                      drag.onPointerDown(event);
                      if (event.button === 0) {
                        barPressRef.current = {x: event.clientX, y: event.clientY};
                      }
                    }
                  },
                  onPointerUp: (event: React.PointerEvent<HTMLElement>) => {
                    drag.onPointerUp(event);
                    const press = barPressRef.current;
                    barPressRef.current = null;
                    // The drag cancels a press's default focus; a click (not a drag) still
                    // moves it here, so Escape reaches the window.
                    if (
                      press &&
                      Math.abs(event.clientX - press.x) + Math.abs(event.clientY - press.y) <
                        BAR_CLICK_SLOP_PX
                    ) {
                      windowRef.current?.focus({preventScroll: true});
                    }
                  }
                }
              : undefined,
          windowControls: (
            <WindowControls
              canDock={!isMobile}
              mode={mode}
              onClose={close}
              onSetMode={changeMode}
              previousMode={previousMode}
            />
          ),
          onSelectType: selectType,
          allowedTypes: calculatorTypesFor(tier)
        }
      : {};

  return createPortal(
    <div
      aria-keyshortcuts={mode === 'expanded' ? undefined : 'F6'}
      aria-label={CALCULATOR_NAMES[type]}
      // Only expanded actually contains focus, so only expanded claims to.
      aria-modal={mode === 'expanded'}
      className={classnames(
        isRendered ? 'flex' : 'hidden',
        // Floating/docked sit under any menu the student opens. Expanded covers
        // menus but not #portal's modals, which lose to this body node at equal z.
        mode === 'expanded' ? 'z-dropdown' : 'z-sticky',
        'background-primary fixed flex-col overflow-hidden',
        {
          // Positioning uses the x-spacing scale throughout: this project sets
          // `--spacing: initial`, so bare numeric utilities (inset-0, end-0)
          // silently generate nothing.
          [`${isBelowGlobalNav ? FLOATING_BELOW_NAV_CLASS : 'top-x11 max-h-[calc(100dvh-var(--spacing-x12))]'} right-x2 rounded-x1 ${HEIGHT_CLASS[type]} translate-x-[var(--calculator-window-x,0px)] translate-y-[var(--calculator-window-y,0px)] ${WIDTH_CLASS[type]} border-primary shadow-modal border`]:
            mode === 'floating',
          // Only the leading edge gets a border: the other three sit flush
          // against the viewport, with nothing to be separated from.
          [`${isBelowGlobalNav ? DOCKED_BELOW_NAV_CLASS : 'top-x0'} bottom-x0 right-x0 border-primary w-[var(--calculator-dock-inline-size,426px)] border-s`]:
            mode === 'docked',
          'top-x0 right-x0 bottom-x0 left-x0': mode === 'expanded'
        }
      )}
      data-testid='calculator-window'
      onKeyDown={handleKeyDown}
      ref={windowRef}
      role='dialog'
      tabIndex={-1}
    >
      {/* The calculators' own boundaries ship inside their chunks, so they
          cannot catch a failed import. Without this one, a stale chunk after a
          deploy would take down the question the student is answering. */}
      <ErrorBoundary
        fallbackRender={() => (
          <div className='gap-x2 p-x4 flex grow flex-col items-center justify-center text-center'>
            <Text>The calculator could not be loaded. Reload the page to try again.</Text>
            <IconButton
              icon='xmark'
              iconStyle='regular'
              label='Close calculator'
              onClick={close}
              size='s'
            />
          </div>
        )}
      >
        {isRendered && <CalculatorTypeAnnouncer name={CALCULATOR_NAMES[type]} />}
        <Suspense
          fallback={
            <div className='p-x4 flex grow items-center justify-center'>
              <LoadingSpinner />
            </div>
          }
        >
          {mountedTypes.includes('basic') && (
            <div
              ref={bodyRefFor('basic')}
              className={
                type === 'basic'
                  ? 'min-h-x0 flex grow items-start justify-center overflow-auto'
                  : 'hidden'
              }
            >
              <BasicCalculatorBody {...chromeFor('basic')} embedded />
            </div>
          )}
          {mountedTypes.includes('graphing') && (
            <div
              ref={bodyRefFor('graphing')}
              className={type === 'graphing' ? 'min-h-x0 flex grow' : 'hidden'}
            >
              <GraphingCalculatorBody
                {...chromeFor('graphing')}
                isActive={type === 'graphing' && isOpen}
                // Every change the canvas must re-measure for, reopening
                // included: while hidden it would have measured 0x0. A
                // ResizeObserver is deliberately not used — see its draw effect.
                layoutKey={`${mode}:${type}:${dockedWidth ?? 'default'}:${isOpen}`}
                scopeShortcutsToRoot
              />
            </div>
          )}
          {mountedTypes.includes('scientific') && (
            <div
              ref={bodyRefFor('scientific')}
              className={
                type === 'scientific'
                  ? 'min-h-x0 flex grow items-start justify-center overflow-auto'
                  : 'hidden'
              }
            >
              <ScientificCalculatorBody {...chromeFor('scientific')} embedded />
            </div>
          )}
        </Suspense>
      </ErrorBoundary>

      {/* z-2 because the graphing body's root sets z-index 1, which paints
          above a z-auto sibling however late it comes in the DOM. */}
      {mode === 'docked' && dockMax > WIDTH_PX[type] && (
        <div
          aria-label='Resize calculator. Use the left and right arrow keys to adjust its width.'
          aria-orientation='vertical'
          aria-valuemax={dockMax}
          aria-valuemin={WIDTH_PX[type]}
          aria-valuenow={dockValue}
          aria-valuetext={dockWidthText(dockValue)}
          className='top-x0 bottom-x0 left-x0 w-x1 hover:background-brand focus-ring-inset z-sticky absolute cursor-ew-resize touch-none'
          data-testid='calculator-window__resize-handle'
          role='separator'
          tabIndex={0}
          {...resize}
        />
      )}
    </div>,
    document.body
  );
};

export default CalculatorWindow;
